import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../Contexts/APIContext";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import ComplaintsFiltersViewModel from "./ViewModels/ComplaintsFiltersViewModel";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import { handleAutoCompleteChange } from "../Utility/FilterChangeHandlers";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ComplaintViewModel from "./ViewModels/ComplaintViewModel";
import Chip from "@mui/material/Chip";

interface IVulnerabilitiesFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function ComplaintsFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: IVulnerabilitiesFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [complaintsFilters, setComplaintsFilters] = useState<ComplaintsFiltersViewModel>(callTableViewModel.filters.complaintsFilters);
    const [complaint, setComplaint] = useState<ComplaintViewModel[]>([]);

    const complaintCategories = ['Complaint', 'EOD']

    useEffect(() => {
        if (open) {
            setComplaintsFilters(callTableViewModel.filters.complaintsFilters);
        }
    }, [open])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('Complaints',
                `GetAllComplaintCategoriesDropdown`,
                (data) => setComplaint(data));
        }
    }, [callTableViewModel.filters.client])

    useEffect(() => {
        if (complaintsFilters.complaintCategory.length === 0) {
            setComplaintsFilters({
                ...complaintsFilters,
                complaint: [],
            });
        }
    }, [complaintsFilters.complaintCategory])

    const inputProps: InputProps[] = [
        {
            name: 'complaintCategory',
            label: 'Complaint Category',
            value: complaintsFilters.complaintCategory,
            options: complaintCategories,
            disabled: false,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'complaint',
            label: 'Complaint',
            value: complaintsFilters.complaint,
            options: complaint
                .filter(f => complaintsFilters.complaintCategory.includes(f.category))
                .map(m => m.complaint),
            disabled: complaintsFilters.complaintCategory.length === 0
                || complaintsFilters.complaintCategory === null,
            type: InputType.AutoCompleteMultiple
        },
    ];

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                {
                    inputProps.type === InputType.AutoCompleteMultiple && !inputProps.disabled &&
                    <>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <Autocomplete
                            multiple
                            fullWidth
                            options={inputProps.options!}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option}
                            value={inputProps.value as string[]}
                            onChange={
                                (_event: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setComplaintsFilters) }}
                            limitTags={3}
                            renderOption={(props, option, { selected }) => {
                                const { ...optionProps } = props;
                                return (
                                    <li {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth />
                            )}
                            renderTags={(value, getTagProps) => {
                                const numTags = value.length;
                                const limitTags = 1;
                                return (
                                    <>
                                        {value.slice(0, limitTags).map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={index}
                                                label={option}
                                            />
                                        ))}

                                        {numTags > limitTags && ` +${numTags - limitTags}`}
                                    </>
                                );
                            }}
                        />
                    </>
                }
            </Grid>)
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.complaintsFilters = complaintsFilters;

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Complaints Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    {
                        inputProps.map(props => getInput(props))
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}